import React from 'react';
import { Select, MenuItem, SelectChangeEvent, Box } from '@mui/material';
import { Language } from './index';
import { useLanguage } from './LanguageContext';
import { BRAND_COLORS, alpha } from '../styles/theme';

// SVG Flag components for each language
const FlagGB: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size * 0.6}
    viewBox="0 0 60 36"
    style={{ borderRadius: 2, display: 'block' }}
  >
    <rect width="60" height="36" fill="#012169" />
    <path d="M0,0 L60,36 M60,0 L0,36" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 L60,36 M60,0 L0,36" stroke="#C8102E" strokeWidth="4" />
    <path d="M30,0 V36 M0,18 H60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0 V36 M0,18 H60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);

const FlagDE: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size * 0.6}
    viewBox="0 0 60 36"
    style={{ borderRadius: 2, display: 'block' }}
  >
    <rect width="60" height="12" y="0" fill="#000" />
    <rect width="60" height="12" y="12" fill="#DD0000" />
    <rect width="60" height="12" y="24" fill="#FFCE00" />
  </svg>
);

const FlagES: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size * 0.6}
    viewBox="0 0 60 36"
    style={{ borderRadius: 2, display: 'block' }}
  >
    <rect width="60" height="9" y="0" fill="#AA151B" />
    <rect width="60" height="18" y="9" fill="#F1BF00" />
    <rect width="60" height="9" y="27" fill="#AA151B" />
  </svg>
);

const FlagFR: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size * 0.6}
    viewBox="0 0 60 36"
    style={{ borderRadius: 2, display: 'block' }}
  >
    <rect width="20" height="36" x="0" fill="#002654" />
    <rect width="20" height="36" x="20" fill="#fff" />
    <rect width="20" height="36" x="40" fill="#CE1126" />
  </svg>
);

const flagComponents: Record<Language, React.FC<{ size?: number }>> = {
  en: FlagGB,
  de: FlagDE,
  es: FlagES,
  fr: FlagFR,
};

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'default' }) => {
  const { language, setLanguage } = useLanguage();

  const handleChange = (event: SelectChangeEvent<Language>) => {
    setLanguage(event.target.value as Language);
  };

  const languages: Language[] = ['en', 'de', 'es', 'fr'];

  const FlagIcon = flagComponents[language];

  return (
    <Select
      value={language}
      onChange={handleChange}
      size="small"
      renderValue={() => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FlagIcon size={22} />
        </Box>
      )}
      sx={{
        minWidth: variant === 'compact' ? 56 : 64,
        height: variant === 'compact' ? 32 : 36,
        backgroundColor: 'transparent',
        '& .MuiSelect-select': {
          py: variant === 'compact' ? 0.5 : 1,
          px: 1,
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.3),
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(BRAND_COLORS.forestGreen.main, 0.5),
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: BRAND_COLORS.forestGreen.main,
        },
      }}
    >
      {languages.map((code) => {
        const Flag = flagComponents[code];
        return (
          <MenuItem key={code} value={code} sx={{ justifyContent: 'center', minWidth: 48 }}>
            <Flag size={22} />
          </MenuItem>
        );
      })}
    </Select>
  );
};
